import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button-tw";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { convertThousandSeparator } from "@/scripts/functions";
import { addresseeNamePriority } from "@/utils/invoice";
import { useCancelPayment } from "@/hooks/invoice/use-cancel-payment";

export function CancelPaymentModal({ open, onOpenChange, invoice, onSuccess }) {
  const { mutate: cancelPayment, isLoading: loading } = useCancelPayment();
  const customerName = addresseeNamePriority(invoice);
  const amount = invoice?.invoice_info?.invoice_total;
  const paymentId = invoice?.invoice_info?.payment_id;

  const handleCancel = () => {
    if (!paymentId) {
      toast.error("Payment ID not found.");
      return;
    }
    cancelPayment(paymentId, {
      onSuccess: () => {
        toast.success("Payment has been cancelled.");
        onSuccess?.();
      },
      onError: () => {
        toast.error("Failed to cancel payment. Please try again.");
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel payment</AlertDialogTitle>
        </AlertDialogHeader>

        <div className="bg-gray-50 rounded-lg px-4 py-3 space-y-1 text-sm">
          <div className="flex gap-x-4">
            <span className="text-gray-500 w-20 shrink-0">Customer</span>
            <span className="font-semibold text-gray-900">{customerName}</span>
          </div>
          <div className="flex gap-x-4">
            <span className="text-gray-500 w-20 shrink-0">Amount</span>
            <span className="font-semibold text-gray-900">
              ${convertThousandSeparator(amount)}
            </span>
          </div>
        </div>

        <AlertDialogDescription className="text-sm text-gray-700">
          This payment hasn't settled in a batch yet, so{" "}
          <strong>no money will move</strong>. The invoice will return to
          unpaid.
        </AlertDialogDescription>

        <div className="flex items-start gap-x-2 text-sm text-gray-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mt-0.5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <span>
            {customerName} will receive an email notification that this payment
            has been canceled.
          </span>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Never mind</AlertDialogCancel>
          <Button
            onClick={handleCancel}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Cancel Payment"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
